import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/DataTableContainer';

import { useDispatch, useSelector } from "react-redux";
import { fetchCustomersData } from "../../store/customers/actions";
import moment from 'moment';
import Swal from 'sweetalert2';
import axios from 'axios';
import { toast } from 'react-toastify';

const DEFAULT_PAGE_SIZE = 10;

const Customers = props => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const { data, loading, error } = useSelector(state => state.Customers);

    useEffect(() => {
        dispatch(fetchCustomersData({ page: pageIndex + 1, limit: pageSize }));
    }, [dispatch, pageIndex, pageSize]);

    const handleViewCustomer = (customerId) => {
        navigate(`/customers/${customerId}`);
    };

    const handleBlockUnblock = async (action, id) => {
      Swal.fire({
        title: `Are you sure you want to ${
          action === "disable" ? "block" : "unblock"
        } the selected user?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#E53935",
        cancelButtonColor: "#3085d6",
        confirmButtonText: `Yes, ${action === "disable" ? "block" : "unblock"} it!`,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            const token = localStorage.getItem("authToken")
            const response = await axios.post(
              `${process.env.REACT_APP_WEB_APP_URL}/api/web/users/${action}/${id}`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            )
            if (response.status == 200) {
              toast.success(
                `User successfully ${
                  action == "disable" ? "blocked" : "unblocked"
                }.`
              )
              dispatch(fetchCustomersData({ page: pageIndex + 1, limit: pageSize }))
            } else {
              toast.error(
                `Failed to ${action == "disable" ? "block" : "unblock"} user`
              )
            }
          } catch (error) {
            if (error.status == 409) {
              return Swal.fire({
                title: `The selected user has ${error?.response?.data?.data?.active_bookings_count} active bookings, blocking the user will cancel all these booking. Confirm block the user?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#E53935",
                cancelButtonColor: "#3085d6",
                confirmButtonText: `Yes, block it!`,
              }).then(async result => {
                if (result.isConfirmed) {
                  try {
                    const formData = new FormData();
                    formData.append("force", 1);
                    const token = localStorage.getItem('authToken')
                    const response2 = await axios.post(
                      `${process.env.REACT_APP_WEB_APP_URL}/api/web/users/${action}/${id}`,
                      formData,
                      {
                        headers: {
                          Accept: 'application/json',
                          Authorization: `Bearer ${token}`,
                          "Content-Type": "multipart/form-data",
                        },
                      }
                    )
                    if (response2.status == 200) {
                      toast.success( response2.message )
                      dispatch(fetchCustomersData({ page: pageIndex + 1, limit: pageSize }))
                    } else {
                      toast.error( `Failed to block user.` )
                    }
                  } catch (error) {
                    toast.error( error.message || `Failed to block user.` )
                  }
                }
              })
            }
            toast.error( error.message || `Failed to ${action == "disable" ? "block" : "unblock"} user` )
          }
        }
      })
    }

    const columns = useMemo(
        () => [
          {
            header: "Image",
            accessorKey: "profile_pic",
            enableColumnFilter: false,
            cell: ({ row }) => (
              <img
                src={row.original.profile_pic || "assets/images/no-image.png"}
                alt="Profile"
                style={{ width: 40, height: 40, borderRadius: "50%" }}
              />
            )
          },
          {
            header: 'Name',
            accessorKey: 'name',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Email',
            accessorKey: 'email',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Phone',
            accessorKey: 'phone',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Is Active?',
            accessorKey: 'is_active',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Created At',
            accessorKey: 'timestamp',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Actions',
            accessorKey: 'actions',
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }) => {
              const userId = row.original?.userId;
              const isActive = row.original?.is_active;
              
              return (
                <div className="d-flex gap-2">
                  {isActive === "YES" ? 
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleBlockUnblock("disable", userId)}
                  >
                  <i className="mdi mdi-block-helper"></i> Block
                </button>
                : 
                <button
                  className="btn btn-sm btn-warning"
                  onClick={() => handleBlockUnblock("enable", userId)}
                  >
                  <i className="mdi mdi-check-circle-outline"></i> Unblock
                </button>
                  }
                  <button
                    className="btn btn-sm btn-info"
                    onClick={() => handleViewCustomer(userId)}
                    title="View Details"
                  >
                    <i className="mdi mdi-eye"></i> View
                  </button>
                </div>
              );
            },
          }
        ],
        []
      );

    const totalRecords = data?.total ?? data?.data?.length ?? 0;

    const handlePaginationChange = (newPageIndex, newPageSize) => {
        setPageIndex(newPageIndex);
        setPageSize(newPageSize);
    };

    const transformedData = data?.data?.map(item => {
        return {
            userId: item.user_id || "N/A",
            profile_pic: item.profile_pic || "assets/images/no-image.png",
            name: item.user_name || "N/A",
            email: item.email || "N/A",
            timestamp: moment(item.timestamp).format("DD-MM-YYYY") || "N/A",
            phone: item.phone_code + " " + item.mobno || "N/A",
            is_active: item.is_active == 1 ? 'YES' : 'NO' || "N/A",
        };
    });
    
    document.title = "Cusomters | Moeen";
    return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="Customers" />
        {loading ? (
        <Skeleton count={10} height={40} />
        ) : (
        <TableContainer
          columns={columns}
          data={transformedData || []}
          isGlobalFilter={true}
          isPagination={true}
          isCustomPageSize={true}
          SearchPlaceholder={`Search in ${totalRecords} results.`}
          pagination="pagination"
          paginationWrapper='dataTables_paginate paging_simple_numbers'
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          serverSidePagination={true}
          totalRowCount={totalRecords}
          pageIndex={pageIndex}
          pageSize={pageSize}
          onPaginationChange={handlePaginationChange}
        />
      )}
      </div>
    </div>
  );
};

Customers.propTypes = {
  t: PropTypes.any
};

export default Customers;